import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useOpenParams, updateStoredAuthToken } from 'app/shared/hooks/use-open-params';
import { HQO_BRAND, HqoHeadersEnum } from 'app/shared/consts/enums';
import { createContext, FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

export interface ApiContextValue {
  apiUrl: string;
  authToken: string;
  setAuthToken: (token: string) => void;
  getAuthTokenValue: () => Promise<string>;
  getAuthHeaderValue: () => Promise<string>;
  getAuthHeaders: () => Promise<Record<string, string>>;
  getDefaultHeaders: (buildingUuid?: string) => Record<string, string>;
}

const ApiContext = createContext<ApiContextValue>({
  apiUrl: process.env.REACT_APP_API_URL || '',
  authToken: '',
  setAuthToken: () => undefined,
  getAuthTokenValue: () => Promise.resolve(''),
  getAuthHeaderValue: () => Promise.resolve(''),
  getAuthHeaders: () => Promise.resolve({}),
  getDefaultHeaders: () => ({}),
});

// Host returns a raw JWT; query-param tokens carry a "bearer " prefix. Normalise both.
const normalizeAuthToken = (token: string): string => {
  if (!token) {
    return '';
  }

  return /^bearer\s/i.test(token) ? token : `Bearer ${token}`;
};

export const ApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { authToken: initialAuthToken, apiUrl, appBrand } = useOpenParams();

  // Normalize the launch token up front — useOpenParams returns it raw, so seeding state/
  // ref unnormalized would emit an `Authorization` header without the `Bearer ` prefix
  // until the first refresh ran normalizeAuthToken.
  const initialToken = normalizeAuthToken(initialAuthToken);

  // Stateful so useTokenRefresh can swap in a fresh token after the host re-auths.
  const [authToken, setAuthTokenState] = useState<string>(initialToken);

  // The getters below must return the LATEST token synchronously. useTokenRefresh's
  // refreshTokenResponse handler calls setAuthToken then immediately invalidateQueries;
  // that refetch runs before React re-renders, so a value closed over `authToken`
  // (state) would still be the stale one and 401 again. The ref is always current.
  const authTokenRef = useRef<string>(initialToken);

  const setAuthToken = useCallback((token: string) => {
    const normalized = normalizeAuthToken(token);
    authTokenRef.current = normalized;
    setAuthTokenState(normalized);
    updateStoredAuthToken(normalized);
  }, []);

  const contextValue = useMemo(
    () => ({
      apiUrl: apiUrl ? new URL(apiUrl).origin : '',
      authToken,
      setAuthToken,
      getAuthTokenValue: async () => authTokenRef.current.replace(/bearer /i, ''),
      getAuthHeaderValue: async () => authTokenRef.current,
      // authTokenRef.current is already `Bearer …`-prefixed via normalizeAuthToken — do NOT re-prefix.
      getAuthHeaders: async () => ({ Authorization: authTokenRef.current }),
      getDefaultHeaders: (buildingUuid?: string): Record<string, string> => ({
        [HqoHeadersEnum.BRAND]: appBrand ?? HQO_BRAND,
        ...(buildingUuid && { [HqoHeadersEnum.HQO_BUILDING_UUID]: buildingUuid }),
      }),
    }),
    [authToken, apiUrl, appBrand, setAuthToken],
  );

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};

export const useApi = <K extends keyof ApiContextValue | undefined = undefined>(key?: K) =>
  useContextValue(ApiContext, key);
